/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { PlanState } from 'src/app/services/plan-state.service';
import * as AppStrings from '@touchplan/strings';
import { PULL_TICKET_WIDTH_SIZES } from '@touchplan/common';
import { GenericDialogData } from '../../projects2/projects2.interfaces';
import { SelectionDialogComponent } from 'src/app/common/generic-dialog/selection-dialog/selection-dialog.component';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import { subjectSubscribeWithoutComplete } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

@Component({
	selector: 'wide-ticket-starter',
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WideTicketStarterComponent implements OnDestroy {
	public widePullTicketEnabled$: Observable<boolean>;
	private shutdownSubject$: Subject<void> = new Subject();

	constructor(
		private dialog: MatDialog,
		private planState: PlanState,
		private featureFlagManager: FeatureFlagManager
	) {
		this.openWideTicketChoicesModal();
		this.widePullTicketEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.WIDE_PULL_TICKET);
	}

	public openWideTicketChoicesModal(): void {
		const wideTicketSize = this.planState.wideTicketSize$.getValue();
		const defaultChoice = wideTicketSize
			? Object.keys(PULL_TICKET_WIDTH_SIZES).find((key) => PULL_TICKET_WIDTH_SIZES[key] === wideTicketSize)
			: AppStrings.NORMAL;
		const data = {
			title: AppStrings.ADJUST_PULL_TICKET_WIDTH_TITLE,
			content: [
				AppStrings.CHANGES_TO_PULL_TASK_CONTENT_STRING_1,
				AppStrings.CHANGES_TO_PULL_TASK_CONTENT_STRING_2,
			],
			choices: Object.keys(PULL_TICKET_WIDTH_SIZES),
			defaultChoice, // Defaults to normal if no existing selection
			yesButtonText: AppStrings.UICONTROL_TEXT_SAVE.toUpperCase(),
			noButtonText: AppStrings.UICONTROL_TEXT_CANCEL.toUpperCase(),
		} as GenericDialogData;

		this.dialog
			.open(SelectionDialogComponent, { data, disableClose: true })
			.afterClosed()
			.pipe(
				distinctUntilChanged(),
				filter((selectionValue) => !!selectionValue),
				map((selectionValue: string) => {
					return PULL_TICKET_WIDTH_SIZES[selectionValue];
				}),
				//this exposes a kind of fun problem with the split data design. Sometimes the api's operate on id's and fetch from their own list
				// and sometimes they just accept whatever instance is passed in. This isn't _normally_ a problem. But when multiple instances of
				// firebase gets are used to get the same data... they no longer share the same instance. And so when an instance is passed in
				// that instance is different than the expected one.
				// ...For now just work around it
				tap((selectionValue) =>
					this.planState.plansApi.setPullTicketSizeOnPlan(
						this.planState.plansApi.data.syncList.get(this.planState.planId),
						selectionValue
					)
				),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.planState.wideTicketSize$));
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
